/**
 * メッセージテンプレートのキャッシュ管理モジュール
 * SWRを使用したキャッシュの生成と更新を管理します
 * @date 2025/06/25
 */
import React from 'react';
import { MessageTemplateId } from 'src/models/messageTemplate';
import { useSWRConfig } from 'swr';
import { MessageTemplateGetItemResponse, MessageTemplateGetListQuery } from './reader';

/**
 * メッセージテンプレート用のキャッシュキージェネレーター
 * リストとアイテム個別のキャッシュキーを生成する機能を提供します
 */
export const messageTemplateCacheKeyGenerator = {
  /**
   * メッセージテンプレート一覧用のキャッシュキーを生成
   * @param query - 一覧取得時のクエリパラメータ
   * @param ignore - キャッシュを無視するかどうかのフラグ
   * @returns キャッシュキーの配列、またはignoreがtrueの場合はundefined
   */
  generateListKey: ({
    query,
    ignore = false,
  }: {
    query?: MessageTemplateGetListQuery;
    ignore?: boolean;
  }) => {
    if (ignore) {
      return;
    }
    return ['MESSAGE_TEMPLATE', 'LIST', ...[query]] as const;
  },

  /**
   * 個別のメッセージテンプレート用のキャッシュキーを生成
   * @param query - テンプレートIDを含むクエリパラメータ
   * @param ignore - キャッシュを無視するかどうかのフラグ
   * @returns キャッシュキーの配列、またはignoreがtrueの場合はundefined
   */
  generateItemKey: ({
    query,
    ignore = false,
  }: {
    query: { messageTemplateId: MessageTemplateId | undefined };
    ignore?: boolean;
  }) => {
    if (ignore) {
      return;
    }
    return ['MESSAGE_TEMPLATE', 'ITEM', query.messageTemplateId] as const;
  },
};

/**
 * メッセージテンプレートのキャッシュ更新用フック
 * キャッシュの手動更新機能を提供します
 * @returns キャッシュ更新用の関数を含むオブジェクト
 */
export const useMessageTemplateCacheMutator = () => {
  // SWRのmutate関数を取得
  const { mutate } = useSWRConfig();

  // メモ化したキャッシュ更新関数を返す
  return React.useMemo(
    () => ({
      /**
       * 一覧キャッシュを更新する関数
       * @param query - 更新対象の一覧のクエリパラメータ
       */
      mutateList: (query: MessageTemplateGetListQuery) =>
        mutate(messageTemplateCacheKeyGenerator.generateListKey({ query: query })),

      /**
       * 個別アイテムのキャッシュを更新する関数
       * @param query - 更新対象のテンプレートID
       * @param newData - 新しいデータ（省略可）
       * @param revalidate - 再検証を行うかどうか（省略可）
       */
      mutateItem: (
        query: { messageTemplateId: MessageTemplateId | undefined },
        newData?: MessageTemplateGetItemResponse,
        revalidate?: boolean
      ) =>
        mutate(
          messageTemplateCacheKeyGenerator.generateItemKey({ query }),
          newData,
          revalidate
        ),
    }),
    [mutate]
  );
};